import { useEffect, useState } from 'react';
import { useStore, useLocalStorage } from 'providers';
import { useFetchFromService, usePageTitle } from 'hooks';
import { Voucher } from 'models';
import { pushAnalyticsEvent } from 'helpers/analytics';
import Layout from 'layouts';
import Loader from 'components/Loader';
import PerkzWalletList from 'components/PerkzWalletList';
import PerkzWalletModal from 'views/modals/PerkzWallet';
import { useMediaQuery } from '@mui/material';
import { theme } from 'assets/themes/zizzi';
import { StyledBox } from './PerkzWallet.styles';

interface ModalProps {
  perkzWallet: boolean;
  qrCode: boolean;
}

function PerkzWallet() {
  usePageTitle('Perkz Wallet');

  const { store } = useStore();
  const { userData, vouchersData } = store;

  const fetchVouchersData = useFetchFromService('vouchersData');

  useEffect(() => {
    fetchVouchersData();
  }, [fetchVouchersData]);

  const { getFromStorage, removeFromStorage } = useLocalStorage();
  const hasNewVouchers = !!getFromStorage<boolean>('hasNewVouchers');

  useEffect(() => {
    if (hasNewVouchers) removeFromStorage('hasNewVouchers');
  }, [hasNewVouchers, removeFromStorage]);

  const [modalOpen, setModalOpen] = useState<ModalProps>({
    perkzWallet: false,
    qrCode: false,
  });

  const [currentVoucher, setCurrentVoucher] = useState<Voucher | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const updatedOfferData = vouchersData?.active.find(
    (offer) => offer.voucherId === currentVoucher?.voucherId
  );

  const hasChanges =
    updatedOfferData && JSON.stringify(updatedOfferData) !== JSON.stringify(currentVoucher);

  if (hasChanges) setCurrentVoucher(updatedOfferData);

  const handleToggleModal = () =>
    setModalOpen({ perkzWallet: !modalOpen.perkzWallet, qrCode: false });

  const handleViewVoucher = (voucher: Voucher) => {
    setCurrentVoucher(voucher);
    handleToggleModal();
    pushAnalyticsEvent({ event: 'walletOpenModal', reward: voucher.title, voucher: voucher.title });
  };

  const perkzWalletModalProps = {
    isOpen: modalOpen.perkzWallet,
    onClose: () => setModalOpen({ perkzWallet: false, qrCode: false }),
    displayModal: () => setModalOpen({ perkzWallet: isMobile, qrCode: true }),
  };

  return (
    <Layout ariaLabel="My Perkz wallet">
      {userData && vouchersData ? (
        <StyledBox>
          <PerkzWalletList vouchers={vouchersData} onViewVoucher={handleViewVoucher} />
          {currentVoucher && (
            <PerkzWalletModal {...perkzWalletModalProps} voucher={currentVoucher} />
          )}
        </StyledBox>
      ) : (
        <Loader />
      )}
    </Layout>
  );
}

export default PerkzWallet;
